import { View, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText } from './Text';

type NumberStepperProps = {
  label?: string;
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  compact?: boolean;
  errorText?: string;
};

export function NumberStepper({
  label,
  value,
  onValueChange,
  min = 0,
  max = 20,
  step = 1,
  disabled = false,
  compact = false,
  errorText,
}: NumberStepperProps) {
  const theme = useTheme();
  const displayValue = Number.isFinite(value) ? Math.round(value) : 0;
  const controlHeight = 44;
  const sideButtonWidth = 44;
  const valueMinWidth = compact ? 72 : 64;
  const iconSize = compact ? 18 : 20;

  const handleDecrement = () => {
    const newValue = Math.max(min, value - step);
    onValueChange(newValue);
  };

  const handleIncrement = () => {
    const newValue = Math.min(max, value + step);
    onValueChange(newValue);
  };

  const canDecrement = value > min && !disabled;
  const canIncrement = value < max && !disabled;

  return (
    <View style={{ gap: theme.spacing(0.75) }}>
      {label && (
        <ThemedText variant="bodyStrong">
          {label}
        </ThemedText>
      )}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          borderWidth: 1,
          borderColor: disabled ? theme.colors.borderMuted : theme.colors.border,
          borderRadius: theme.radii.md,
          backgroundColor: theme.colors.surface,
          overflow: 'hidden',
          minWidth: sideButtonWidth * 2 + valueMinWidth,
          flexShrink: 1,
        }}
      >
        <Pressable
          onPress={handleDecrement}
          disabled={!canDecrement}
          style={({ pressed }) => ({
            width: sideButtonWidth,
            backgroundColor: canDecrement && pressed ? theme.colors.background : 'transparent',
            opacity: canDecrement ? (pressed ? 0.7 : 1) : 0.3,
            minHeight: controlHeight,
            justifyContent: 'center',
            alignItems: 'center',
          })}
        >
          <Ionicons 
            name="remove-outline" 
            size={iconSize} 
            color={canDecrement ? theme.colors.text : theme.colors.textSecondary} 
          />
        </Pressable>

        <View
          style={{
            flex: 1,
            paddingVertical: compact ? theme.spacing(1) : theme.spacing(1.5),
            paddingHorizontal: compact ? theme.spacing(1) : theme.spacing(1.5),
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: controlHeight,
            minWidth: valueMinWidth,
            borderLeftWidth: 1,
            borderRightWidth: 1,
            borderLeftColor: theme.colors.borderMuted,
            borderRightColor: theme.colors.borderMuted,
          }}
        >
          <ThemedText
            variant="bodyStrong"
            style={{ fontSize: compact ? 16 : 18, fontVariant: ['tabular-nums'] }}
            numberOfLines={1}
          >
            {displayValue}
          </ThemedText>
        </View>

        <Pressable
          onPress={handleIncrement}
          disabled={!canIncrement}
          style={({ pressed }) => ({
            width: sideButtonWidth,
            backgroundColor: canIncrement && pressed ? theme.colors.background : 'transparent',
            opacity: canIncrement ? (pressed ? 0.7 : 1) : 0.3,
            minHeight: controlHeight,
            justifyContent: 'center',
            alignItems: 'center',
          })}
        >
          <Ionicons 
            name="add-outline" 
            size={iconSize} 
            color={canIncrement ? theme.colors.text : theme.colors.textSecondary} 
          />
        </Pressable>
      </View>
      {errorText ? (
        <ThemedText variant="caption" style={{ color: theme.colors.error }}>
          {errorText}
        </ThemedText>
      ) : null}
    </View>
  );
}

